import { z } from "zod/v4";

import {
  AgenticLogicOrderDataDescriptions,
  AgenticLogicOrderLineDescriptions,
  AgenticLogicOrderLineDiscountAllocationDescriptions,
} from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";
import { AgenticLogicCartLineSchema } from "./cart";

export const AgenticLogicOrderLineDiscountAllocationSchema = z
  .object({
    amount: z
      .number()
      .int()
      .nonnegative()
      .describe(AgenticLogicOrderLineDiscountAllocationDescriptions.amount),
    title: z
      .string()
      .optional()
      .describe(AgenticLogicOrderLineDiscountAllocationDescriptions.title),
  })
  .describe(AgenticLogicOrderLineDiscountAllocationDescriptions._schema);
export type AgenticLogicOrderLineDiscountAllocation = z.infer<
  typeof AgenticLogicOrderLineDiscountAllocationSchema
>;

export const AgenticLogicOrderLineSchema = AgenticLogicCartLineSchema.extend({
  title: z
    .string()
    .optional()
    .describe(AgenticLogicOrderLineDescriptions.title),
  vendor: z
    .string()
    .optional()
    .describe(AgenticLogicOrderLineDescriptions.vendor),
  productType: z
    .string()
    .optional()
    .describe(AgenticLogicOrderLineDescriptions.productType),
  sellingPlanId: z
    .string()
    .optional()
    .describe(AgenticLogicOrderLineDescriptions.sellingPlanId),
  discountAllocations: z
    .array(AgenticLogicOrderLineDiscountAllocationSchema)
    .optional()
    .describe(AgenticLogicOrderLineDescriptions.discountAllocations),
}).describe(AgenticLogicOrderLineDescriptions._schema);
export type AgenticLogicOrderLine = z.infer<typeof AgenticLogicOrderLineSchema>;

// NB: deliberately NO `customer` field on the order — the top-level request
// `customer` is the single customer reference (identity-merge semantics are
// undecided; omitting is the non-inventing choice).
export const AgenticLogicOrderDataSchema = z
  .object({
    orderId: z.string().describe(AgenticLogicOrderDataDescriptions.orderId),
    checkoutToken: z
      .string()
      .optional()
      .describe(AgenticLogicOrderDataDescriptions.checkoutToken),
    createdAt: z.iso
      .datetime()
      .describe(AgenticLogicOrderDataDescriptions.createdAt),
    currencyCode: z
      .string()
      .describe(AgenticLogicOrderDataDescriptions.currencyCode),
    presentmentCurrency: z
      .string()
      .describe(AgenticLogicOrderDataDescriptions.presentmentCurrency),
    itemsSubtotal: z
      .number()
      .int()
      .nonnegative()
      .describe(AgenticLogicOrderDataDescriptions.itemsSubtotal),
    discountTotal: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(AgenticLogicOrderDataDescriptions.discountTotal),
    shippingTotal: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(AgenticLogicOrderDataDescriptions.shippingTotal),
    taxTotal: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(AgenticLogicOrderDataDescriptions.taxTotal),
    total: z
      .number()
      .int()
      .nonnegative()
      .describe(AgenticLogicOrderDataDescriptions.total),
    lines: z
      .array(AgenticLogicOrderLineSchema)
      .describe(AgenticLogicOrderDataDescriptions.lines),
  })
  .describe(AgenticLogicOrderDataDescriptions._schema);
export type AgenticLogicOrderData = z.infer<typeof AgenticLogicOrderDataSchema>;

assertDescriptions<
  typeof AgenticLogicOrderLineDiscountAllocationDescriptions,
  AgenticLogicOrderLineDiscountAllocation
>();
assertDescriptions<
  typeof AgenticLogicOrderLineDescriptions,
  AgenticLogicOrderLine
>();
assertDescriptions<
  typeof AgenticLogicOrderDataDescriptions,
  AgenticLogicOrderData
>();
